import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { imageurl } from "../../App";
import {
  getGuestCartTotals,
  updateGuestCartQty,
  removeGuestCartItem,
  CART_UPDATED_EVENT,
  DRAWER_TOGGLE_EVENT,
  closeCartDrawer
} from "../../util/guestCartService";
import { getProductImageUrl, handleProductImageError } from "../../util/storeLogoHelper";
import { cartDrawerSettings, cartShippingLine } from "../../themes/cartDrawerSettings";
import {
  FaTimes,
  FaShoppingBag,
  FaArrowRight,
  FaTrashAlt,
  FaTruck,
  FaLock
} from "react-icons/fa";
import CartRecommendations from "./CartRecommendations";

export default function CartDrawer({ themeSettings = null }) {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [cartData, setCartData] = useState(() => getGuestCartTotals());
  const look = cartDrawerSettings(themeSettings || {});
  const checkoutButton = {
    backgroundColor: look.checkoutBackground,
    color: look.checkoutText,
    borderRadius: look.checkoutRadius,
    border: `1px solid ${look.checkoutBorder}`,
  };
  const viewButton = {
    backgroundColor: look.viewBackground,
    color: look.viewText,
    borderRadius: look.checkoutRadius,
    border: `1px solid ${look.viewBorder}`,
  };

  useEffect(() => {
    const handleDrawerEvent = (e) => {
      if (e.detail?.open !== undefined) {
        setIsOpen(e.detail.open);
      } else {
        setIsOpen((prev) => !prev);
      }
    };

    const handleCartUpdate = () => {
      setCartData(getGuestCartTotals());
    };

    window.addEventListener(DRAWER_TOGGLE_EVENT, handleDrawerEvent);
    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdate);

    return () => {
      window.removeEventListener(DRAWER_TOGGLE_EVENT, handleDrawerEvent);
      window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    closeCartDrawer();
  };

  const handleProceedToCheckout = () => {
    handleClose();
    history.push("/checkout");
  };

  const { items, subtotal, totalSavings, remainingForFreeShipping, freeShippingProgress, totalCount, freeShippingThreshold } = cartData;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
        style={{ zIndex: 1050, backdropFilter: "blur(2px)", animation: "fadeIn 0.2s ease" }}
        onClick={handleClose}
      />

      {/* Slide-out Drawer Panel */}
      <div
        className="position-fixed top-0 h-100 shadow-lg d-flex flex-column"
        style={{
          width: "100%",
          maxWidth: "420px",
          zIndex: 1060,
          background: look.background,
          color: look.headingColor,
          animation: look.side === "left" ? "slideInLeft 0.25s ease-out" : "slideInRight 0.25s ease-out",
          [look.side === "left" ? "left" : "right"]: 0,
        }}
      >
        <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <FaShoppingBag size={18} color={look.iconColor} />
            <h5 className="fw-bold mb-0" style={{ color: look.headingColor }}>
              {look.heading} ({totalCount})
            </h5>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-light rounded-circle p-2 d-flex align-items-center justify-content-center"
            onClick={handleClose}
            style={{ width: "32px", height: "32px" }}
          >
            <FaTimes size={14} />
          </button>
        </div>

        {look.showShipping && (
        <div className="p-3 border-bottom" style={{ background: look.shippingBackground, color: look.shippingText }}>
          <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "12.5px" }}>
            <span className="fw-semibold d-flex align-items-center gap-1">
              <FaTruck color={look.iconColor} />
              <span
                className="fw-bold"
                style={{
                  color:
                    Number(freeShippingThreshold) === 0 || Number(remainingForFreeShipping) === 0
                      ? look.shippingFreeColor
                      : look.shippingAddColor,
                }}
              >
                {cartShippingLine(look, freeShippingThreshold, remainingForFreeShipping)}
              </span>
            </span>
            <span className="fw-bold">{Math.round(freeShippingProgress)}%</span>
          </div>
          <div className="progress" style={{ height: "6px", borderRadius: "3px", background: "#e5e7eb" }}>
            <div
              className="progress-bar"
              style={{ width: `${freeShippingProgress}%`, background: look.shippingBar }}
            />
          </div>
        </div>
        )}

        {/* Drawer Items List */}
        <div className="flex-grow-1 overflow-auto p-3 d-flex flex-column gap-3">
          {items.length === 0 ? (
            <div className="text-center py-5 my-auto">
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                style={{ width: "64px", height: "64px", background: "var(--accent-light)", color: look.iconColor }}
              >
                <FaShoppingBag size={26} />
              </div>
              <h5 className="fw-bold mb-1" style={{ color: look.headingColor }}>{look.emptyTitle}</h5>
              <p className="mb-4" style={{ fontSize: "13.5px", color: look.textColor }}>
                {look.emptyMessage}
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="btn px-4 py-2 fw-semibold"
                style={{ ...checkoutButton, fontSize: "13.5px" }}
              >
                {look.emptyButton}
              </button>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="p-2 rounded-3 d-flex gap-3 align-items-center"
                style={{
                  background: look.itemBackground,
                  border: `1px solid ${look.itemBorder}`,
                  boxShadow: "var(--shadow-xs)",
                }}
              >
                {/* Thumbnail */}
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "var(--radius-md)",
                    overflow: "hidden",
                    border: `1px solid ${look.itemBorder}`,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={getProductImageUrl(item.image)}
                    alt={item.name}
                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: "4px" }}
                    onError={handleProductImageError}
                  />
                </div>

                {/* Info */}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <h6 className="fw-bold mb-1 text-truncate" style={{ fontSize: "13.5px", color: look.itemName }}>
                    {item.name}
                  </h6>
                  <div className="d-flex align-items-baseline gap-2 mb-2">
                    <span className="fw-bold" style={{ fontSize: "14px", color: look.itemPrice }}>
                      Rs. {Number(item.price).toLocaleString()}
                    </span>
                    {item.hasDiscount && (
                      <span className="text-decoration-line-through" style={{ fontSize: "12px", color: look.itemOldPrice }}>
                        Rs. {Number(item.originalPrice).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Qty & Remove */}
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="qty-stepper" style={{ height: "30px", background: look.qtyBackground, color: look.qtyText, borderRadius: "8px" }}>
                      <button
                        type="button"
                        className="qty-btn"
                        style={{ width: "26px", fontSize: "14px", color: look.qtyText, background: "transparent" }}
                        onClick={() => updateGuestCartQty(item.id, item.qty - 1)}
                      >
                        -
                      </button>
                      <div className="qty-value" style={{ width: "28px", fontSize: "13px", color: look.qtyText }}>
                        {item.qty}
                      </div>
                      <button
                        type="button"
                        className="qty-btn"
                        style={{ width: "26px", fontSize: "14px", color: look.qtyText, background: "transparent" }}
                        onClick={() => updateGuestCartQty(item.id, item.qty + 1)}
                      >
                        +
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeGuestCartItem(item.id)}
                      className="btn btn-link p-0 d-flex align-items-center"
                      style={{ color: look.removeColor }}
                      title="Remove Item"
                    >
                      <FaTrashAlt size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
          {look.showRecommendations && items.length > 0 && (
            <CartRecommendations items={items} title={look.recommendationsTitle} compact onNavigate={closeCartDrawer} />
          )}
        </div>

        {/* Drawer Footer & Checkout Action */}
        {items.length > 0 && (
          <div className="p-3 border-top">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span style={{ fontSize: "13.5px", color: look.subtotalLabelColor }}>{look.subtotalLabel}</span>
              <span className="fw-bold" style={{ fontSize: "16px", color: look.subtotalColor }}>
                Rs. {Number(subtotal).toLocaleString()}
              </span>
            </div>

            {look.showSavings && totalSavings > 0 && (
              <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: "13px", color: look.savingsColor }}>
                <span>{look.savingsLabel}</span>
                <span>-Rs. {Number(totalSavings).toLocaleString()}</span>
              </div>
            )}

            {look.note ? (
              <p className="mb-3" style={{ fontSize: "11.5px", color: look.textColor }}>
                {look.note}
              </p>
            ) : null}

            <div className="d-flex flex-column gap-2">
              <button
                type="button"
                onClick={handleProceedToCheckout}
                className="btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2"
                style={{ ...checkoutButton, fontSize: "15px" }}
              >
                <FaLock size={13} /> {look.checkoutLabel} • Rs. {Number(subtotal).toLocaleString()} <FaArrowRight size={13} />
              </button>

              {look.showViewCart && (
                <Link
                  to="/cart"
                  onClick={handleClose}
                  className="btn w-100 py-2 fw-semibold"
                  style={{ ...viewButton, fontSize: "13px" }}
                >
                  {look.viewCartLabel}
                </Link>
              )}
              {look.menus.map((item) => {
                const external = /^https?:\/\//i.test(item.link);
                const className = "btn btn-link w-100 py-1 fw-semibold text-decoration-none";
                const style = { fontSize: "13px", color: look.linkColor };
                if (external) {
                  return (
                    <a
                      key={item.id}
                      href={item.link}
                      className={className}
                      style={style}
                      target="_blank"
                      rel="noreferrer"
                      onClick={handleClose}
                    >
                      {item.label}
                    </a>
                  );
                }
                return (
                  <Link key={item.id} to={item.link} className={className} style={style} onClick={handleClose}>
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
